import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, HelpCircle } from 'lucide-react';

const GUIDES = [
  { title: 'GET STARTED IN 3 MINUTES', steps: ['Sign in with Google.', 'Upload your resume, ID and a recent payslip on the Documents page, then click Parse on each one.', 'Check your 7-tab Life Profile on the Dashboard and fill any gaps flagged by the Missing Data Engine.'] },
  { title: 'AUTOFILL ANY WEBSITE', steps: ['Install the browser extension from the Extension page (Chrome, Edge, Brave or Firefox).', 'Visit lifeautofill.com once while signed in so the extension can link to your account.', 'Open any form, such as a Seek job, a realestate.com.au rental or a gym sign-up, and click the floating Autofill button.'] },
  { title: 'APPLY FOR 20 RENTALS IN AN HOUR', steps: ['Upload your ID, payslips and rental history once, and fill the Rental History and References tabs.', 'Open each listing\'s application and click Autofill. Documents attach automatically.', 'Click Next through each step (it keeps filling), answer the orange highlighted questions, then submit.'] },
];

const FAQS = [
  {
    q: 'Do I ever have to type anything into a form?',
    a: 'Almost never. One click fills every field that matches something in your Life Profile. Fields we have never seen before are worked out by AI from their labels, and upload boxes for ID, payslips or a resume are filled from your document vault. Multi-step forms keep filling as you click Next. The only things left (highlighted in orange) are questions no profile can answer, like your preferred move-in date or how you heard about the property.',
  },
  {
    q: 'What is Form Memory?',
    a: 'Every website names its form fields differently. After you autofill a page, any field you fix or fill in by hand is remembered for that website. If what you typed matches something in your profile (like your phone number), we save which profile item belongs there rather than the value itself, so it stays up to date when you change your profile. You can view and delete everything that has been learned on the Extension page. Free plans remember 3 websites; Pro remembers unlimited websites.',
  },
  {
    q: 'Which browsers does the extension support?',
    a: 'Chrome, Microsoft Edge, Brave and other Chromium browsers, plus Firefox 142 or newer. Until our store listings are approved, you install it from a .zip using the steps on the Extension page. On Firefox, temporary add-ons are removed when the browser restarts.',
  },
  {
    q: 'The Autofill button does not appear on a page. What should I do?',
    a: 'Make sure the floating button is turned on in the extension popup, then refresh the page. Some sites only show their form after you click "Apply" or go to the next step, and the button appears once the fields load. You can also right-click the page and choose "Autofill this page", or click the extension icon and press "Fill this page".',
  },
  {
    q: 'Which Australian forms does it understand?',
    a: 'The extension recognises 80+ field types used on Australian forms, including TFN, Medicare number, driver licence number and state, BSB, weekly rent, previous landlord or agent, reason for leaving, right to work, notice period, referees and next of kin. It also handles date of birth split across separate day/month/year boxes, as well as DD/MM/YYYY dates.',
  },
  {
    q: 'How do I cancel my subscription?',
    a: 'Go to Settings → Subscription → Manage / Cancel. This opens Stripe\'s secure portal where you can cancel, update your card, or view all past invoices. Cancellation takes effect at the end of your current billing period — you keep Pro access until then.',
  },
  {
    q: 'How does the AI document parsing work?',
    a: 'When you click "Parse" we send that one document to a state-of-the-art AI model. Text-heavy documents (resumes, payslips, bank statements, tax records) go to Claude Sonnet 4.5; image-heavy documents (IDs, passports, driver\'s licenses) go to Gemini 2.5 Flash. The AI extracts structured data into your profile in 5–20 seconds.',
  },
  {
    q: 'Is my data safe?',
    a: 'Documents are stored in encrypted object storage. Profile data lives in an encrypted MongoDB instance. Sessions are bound to secure HTTP-only cookies. We do not sell your data and we do not use your documents to train ML models. See our Privacy Policy for full details.',
  },
  {
    q: 'Why didn\'t it fill field X on my application?',
    a: 'The autofill engine can only fill fields it has data for. Open the Dashboard — the "Missing Data Engine" panel shows which fields are blocking the most templates. Upload additional documents (e.g. a payslip to fill income), or edit your profile directly under Identity / Work / Financials.',
  },
  {
    q: 'How do I get a refund?',
    a: 'Per our Terms, we don\'t pro-rate partial months. If you cancel mid-month you keep Pro access until the period ends, then we won\'t bill you again. For exceptional cases (accidental signup, billing error), email us via the Contact page within 7 days of charge and we\'ll review.',
  },
  {
    q: 'Can I delete my account?',
    a: 'Yes — Settings → Danger Zone → Delete Account. You\'ll be asked to type DELETE to confirm. This removes your profile, all uploaded documents, all applications, and cancels any active subscription. It cannot be undone.',
  },
  {
    q: 'What document types are supported?',
    a: 'Resume / CV, ID card, passport, driver\'s license, bank statement, payslip, tax record, certification, and a generic "other" type. Supported file formats: PDF, DOC, DOCX, PNG, JPG, JPEG, WEBP. Max 10 MB per file.',
  },
  {
    q: 'Why is parsing taking so long?',
    a: '5 to 20 seconds is normal — large PDFs or detailed extractions can take up to 30 seconds. If a parse fails or hangs past 60 seconds, refresh and try again. Persistent issues? Use the Contact page.',
  },
  {
    q: 'Does this work outside Australia?',
    a: 'Yes. AI document extraction works on documents from any country, and common fields like name, address, employer and income work everywhere. The field library is tuned for Australian sites first, and Form Memory fills in the gaps on any site in any country.',
  },
  {
    q: 'How many autofills do I get on the Free plan?',
    a: 'Free includes 2 autofills per month, Form Memory for 3 websites, and AI parsing of your documents. Pro is US$9.99/month and adds unlimited autofills, unlimited Form Memory, Application Packs and the AI Application Coach.',
  },
  {
    q: 'Can I use it on my phone?',
    a: 'Yes. Open lifeautofill.com in your mobile browser and tap "Add to Home Screen" when prompted (on iPhone: Share, then Add to Home Screen). Browser extensions are not available on mobile, so use the Fill Helper to copy values into forms with one tap.',
  },
];

export default function Help() {
  const [open, setOpen] = useState(0);

  return (
    <div className="container mx-auto px-4 md:px-8 py-12 max-w-3xl" data-testid="help-page">
      <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground mb-8">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <HelpCircle className="w-6 h-6 text-primary" />
        <h1 className="font-heading text-4xl">HELP CENTER</h1>
      </div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
        Guides and answers to the most common questions
      </p>
      <p className="text-muted-foreground leading-relaxed mb-10">
        Life Admin Autofill turns your documents into one secure Life Profile, then fills in job, rental, loan and government forms for you.
        Start with the quick guides below, then browse the FAQ. If you can&apos;t find an answer, a real person will reply within 24 hours.
      </p>

      <div className="grid md:grid-cols-3 gap-4 mb-12" data-testid="help-guides">
        {GUIDES.map((g, gi) => (
          <div key={g.title} className="border-2 border-border p-5" data-testid={`help-guide-${gi}`}>
            <h2 className="font-heading text-xs uppercase tracking-widest text-primary mb-3">{g.title}</h2>
            <ol className="list-decimal pl-4 space-y-2 text-sm text-muted-foreground">
              {g.steps.map((st) => <li key={st}>{st}</li>)}
            </ol>
          </div>
        ))}
      </div>

      <h2 className="font-heading text-sm uppercase tracking-widest mb-4">FREQUENTLY ASKED QUESTIONS</h2>

      <div className="border border-border" data-testid="faq-list">
        {FAQS.map((f, i) => (
          <button
            key={i}
            onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full text-left border-b border-border last:border-b-0 hover:bg-secondary/20 transition-colors"
            data-testid={`faq-${i}`}
          >
            <div className="flex items-center justify-between gap-4 px-5 py-4">
              <span className="font-medium text-sm">{f.q}</span>
              <ChevronDown
                className={`w-4 h-4 flex-shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`}
              />
            </div>
            {open === i && (
              <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
            )}
          </button>
        ))}
      </div>

      <div className="mt-10 border border-dashed border-border p-6 text-center" data-testid="help-still-stuck">
        <p className="text-sm text-muted-foreground mb-3">Still stuck?</p>
        <Link to="/contact" className="inline-block px-5 py-2 border border-primary text-primary uppercase text-xs tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors">
          CONTACT SUPPORT
        </Link>
      </div>
    </div>
  );
}
